import _defineProperty from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path1/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/slicedToArray.js";
import React from "react";
var __jsx = React.createElement;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getSelection, $isRangeSelection, $isTextNode } from "lexical";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { $createVariableNode } from "./variable-node";
var MENU_MAX_HEIGHT = 240;
var MENU_MIN_WIDTH = 220;

/** `@`-triggered typeahead that inserts an auto-population variable node at the cursor. */
export function VariableTypeaheadPlugin(_ref) {
  var variables = _ref.variables;
  var _useLexicalComposerCo = useLexicalComposerContext(),
    _useLexicalComposerCo2 = _slicedToArray(_useLexicalComposerCo, 1),
    editor = _useLexicalComposerCo2[0];
  var _useState = useState(null),
    mention = _useState[0],
    setMention = _useState[1];
  var _useState2 = useState(0),
    selectedIndex = _useState2[0],
    setSelectedIndex = _useState2[1];
  var selectedIndexRef = useRef(0);
  var filteredRef = useRef([]);
  var filtered = useMemo(function () {
    if (!mention) return [];
    var query = mention.query.toLowerCase();
    return variables.filter(function (variable) {
      return variable.label.toLowerCase().includes(query) || variable.key.toLowerCase().includes(query);
    });
  }, [mention, variables]);
  filteredRef.current = filtered;
  selectedIndexRef.current = selectedIndex;
  var insertVariable = useCallback(function (variable) {
    trackEvent(ANALYTICS_EVENTS.LETTER_VARIABLE);
    editor.update(function () {
      var selection = $getSelection();
      if (!$isRangeSelection(selection)) return;
      var anchorNode = selection.anchor.getNode();
      if (!$isTextNode(anchorNode)) return;
      var anchorOffset = selection.anchor.offset;
      var textContent = anchorNode.getTextContent();
      var atIndex = -1;
      for (var i = anchorOffset - 1; i >= 0; i--) {
        if (textContent[i] === "@") {
          atIndex = i;
          break;
        }
        if (/\s/.test(textContent[i])) break;
      }
      if (atIndex === -1) return;
      anchorNode.spliceText(atIndex, anchorOffset - atIndex, "", true);
      var next = $getSelection();
      if ($isRangeSelection(next)) next.insertNodes([$createVariableNode(variable.label)]);
    });
    setMention(null);
    setSelectedIndex(0);
  }, [editor]);
  useEffect(function () {
    return editor.registerUpdateListener(function (_ref2) {
      var editorState = _ref2.editorState;
      editorState.read(function () {
        var selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) return setMention(null);
        var anchorNode = selection.anchor.getNode();
        if (!$isTextNode(anchorNode)) return setMention(null);
        var textBefore = anchorNode.getTextContent().slice(0, selection.anchor.offset);
        var atIndex = -1;
        for (var i = textBefore.length - 1; i >= 0; i--) {
          if (textBefore[i] === "@") {
            if (i === 0 || /\s/.test(textBefore[i - 1])) atIndex = i;
            break;
          }
          if (/\s/.test(textBefore[i])) break;
        }
        if (atIndex === -1) return setMention(null);
        var domSel = window.getSelection();
        if (!domSel || domSel.rangeCount === 0) return setMention(null);
        var rect = domSel.getRangeAt(0).getBoundingClientRect();
        setMention({
          query: textBefore.slice(atIndex + 1),
          anchorTop: rect.top,
          anchorBottom: rect.bottom,
          left: rect.left
        });
        setSelectedIndex(0);
      });
    });
  }, [editor]);
  useEffect(function () {
    if (!mention) return;
    var onKeyDown = function onKeyDown(event) {
      var options = filteredRef.current;
      if (!options.length) {
        if (event.key === "Escape") setMention(null);
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(function (index) {
          return Math.min(index + 1, options.length - 1);
        });
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        event.stopPropagation();
        setSelectedIndex(function (index) {
          return Math.max(index - 1, 0);
        });
      } else if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();
        event.stopPropagation();
        insertVariable(options[selectedIndexRef.current]);
      } else if (event.key === "Escape") {
        event.preventDefault();
        setMention(null);
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return function () {
      return document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [mention, insertVariable]);
  if (!mention || !filtered.length) return null;
  var openUpward = window.innerHeight - mention.anchorBottom - 4 < MENU_MAX_HEIGHT;
  var left = Math.min(mention.left, window.innerWidth - MENU_MIN_WIDTH - 8);
  var position = openUpward ? {
    bottom: window.innerHeight - mention.anchorTop + 4,
    left: left
  } : {
    top: mention.anchorBottom + 4,
    left: left
  };
  return /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement("div", {
    "data-testid": "letter-variable-menu",
    className: "fixed max-h-60 min-w-[220px] overflow-y-auto rounded-sds-200 border border-sds-base-default bg-sds-base-default py-1 shadow-sds-400",
    style: _objectSpread(_objectSpread({}, position), {}, {
      zIndex: 1300
    })
  }, filtered.map(function (variable, index) {
    return /*#__PURE__*/React.createElement("div", {
      key: variable.key,
      "data-testid": "letter-variable-option-".concat(variable.key),
      className: "cursor-pointer px-4 py-2 text-style-body-small ".concat(index === selectedIndex ? "bg-sds-brand-tertiary text-sds-brand-default" : "text-sds-base-default hover:bg-sds-base-secondary"),
      onMouseDown: function onMouseDown(event) {
        event.preventDefault();
        insertVariable(variable);
      },
      onMouseEnter: function onMouseEnter() {
        return setSelectedIndex(index);
      }
    }, variable.label);
  })), document.body);
}